"use client";

import React, { useCallback, useEffect, useState } from "react";
import { Ban, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { toast } from "react-hot-toast";
import { Button } from "../../components/ui/button";
import {
  createBlockedCandidate,
  getBlockedCandidates,
  unblockBlockedCandidate,
  type BlockedCandidateRecord,
} from "../../services/blockedCandidatesApi";

function blockedByLabel(blockedBy: BlockedCandidateRecord["blockedBy"]): string {
  if (!blockedBy) return "—";
  if (typeof blockedBy === "string") return blockedBy;
  const name = blockedBy.username?.trim();
  const email = blockedBy.email?.trim();
  if (name && email) return `${name} (${email})`;
  return name || email || "—";
}

function formatDate(value?: string): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function normalizePhoneInput(value: string): string {
  const digits = value.replace(/\D/g, "");
  return digits.length >= 10 ? digits.slice(-10) : digits;
}

const BlockedCandidatesPage: React.FC = () => {
  const [rows, setRows] = useState<BlockedCandidateRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const [phone, setPhone] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [unblockingId, setUnblockingId] = useState<string | null>(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getBlockedCandidates({
        page,
        limit,
        search: search || undefined,
      });
      setRows(res.data || []);
      setTotalPages(res.totalPages || 1);
      setTotalResults(res.totalResults || 0);
    } catch {
      setRows([]);
      setTotalPages(1);
      setTotalResults(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const normalized = normalizePhoneInput(phone);
    if (!/^[6-9]\d{9}$/.test(normalized)) {
      toast.error("Enter a valid 10-digit mobile number starting with 6–9.");
      return;
    }
    setSubmitting(true);
    try {
      await createBlockedCandidate({
        phone: normalized,
        reason: reason.trim() || undefined,
      });
      setPhone("");
      setReason("");
      setPage(1);
      await fetchRows();
    } catch {
      // toast handled in API
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnblock = async (id: string) => {
    setUnblockingId(id);
    try {
      await unblockBlockedCandidate(id);
      await fetchRows();
    } catch {
      // toast handled in API
    } finally {
      setUnblockingId(null);
    }
  };

  return (
    <div className="flex h-full flex-col gap-4 p-4 md:p-6">
      <div className="flex items-center gap-2">
        <Ban className="h-6 w-6 text-red-600" />
        <h1 className="text-xl font-semibold text-gray-900 md:text-2xl">
          Blocked Candidates
        </h1>
      </div>
      <p className="text-sm text-muted-foreground">
        Block mobile numbers so they cannot be added from Approaches or Add
        Candidate.
      </p>

      <form
        onSubmit={handleAdd}
        className="grid gap-3 rounded-lg border bg-white p-4 md:grid-cols-[180px_1fr_auto]"
      >
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            Mobile number
          </label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="10-digit number"
            className="w-full rounded-md border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-orange-200"
            required
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            Reason (optional)
          </label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this number blocked?"
            className="w-full rounded-md border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-orange-200"
            maxLength={500}
          />
        </div>
        <div className="flex items-end">
          <Button
            type="submit"
            disabled={submitting || !/^[6-9]\d{9}$/.test(normalizePhoneInput(phone))}
            className="w-full md:w-auto"
          >
            {submitting ? "Blocking…" : "Block number"}
          </Button>
        </div>
      </form>

      <form
        onSubmit={handleSearchSubmit}
        className="flex flex-wrap items-center gap-2"
      >
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by phone"
            className="w-full rounded-md border py-2 pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-orange-200"
          />
        </div>
        <Button type="submit" variant="outline">
          Search
        </Button>
        {search ? (
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setSearchInput("");
              setSearch("");
              setPage(1);
            }}
          >
            Clear
          </Button>
        ) : null}
      </form>

      <div className="min-h-0 flex-1 overflow-auto rounded-lg border bg-white">
        <table className="min-w-full text-left text-sm">
          <thead className="sticky top-0 bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-4 py-3 font-medium">Phone</th>
              <th className="px-4 py-3 font-medium">Reason</th>
              <th className="px-4 py-3 font-medium">Blocked By</th>
              <th className="px-4 py-3 font-medium">Date</th>
              <th className="px-4 py-3 font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-gray-500">
                  No blocked numbers yet. Add a mobile number above.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row._id} className="border-t">
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {row.phone}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {row.reason?.trim() || "—"}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {blockedByLabel(row.blockedBy)}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {formatDate(row.createdAt)}
                  </td>
                  <td className="px-4 py-3">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={unblockingId === row._id}
                      onClick={() => handleUnblock(row._id)}
                    >
                      {unblockingId === row._id ? "Unblocking…" : "Unblock"}
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between gap-2 text-sm text-gray-600">
        <span>
          {totalResults} result{totalResults === 1 ? "" : "s"}
        </span>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span>
            Page {page} of {totalPages}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BlockedCandidatesPage;
